/**
 * `window.PlutoniosChat` — the control surface we hand to the customer's own
 * site. The widget lives inside a shadow root, so the host page has no way to
 * reach into it; this object is the only supported bridge.
 *
 *   PlutoniosChat.open({ origin: buttonEl })
 *   PlutoniosChat.close()
 *   PlutoniosChat.toggle()
 *   PlutoniosChat.sendMessage("Voglio sapere di piu' sulle camere", { origin: buttonEl })
 *   PlutoniosChat.on("reply", ({ text }) => ...)
 *   PlutoniosChat.setLanguage("en")
 *
 * The hard part is timing, not surface: a host page wires its click handlers on
 * DOMContentLoaded and the guest can click before our bundle has finished
 * downloading. So every call is queued until the widget mounts and replayed in
 * order — the embed script installs a minimal stub for the window between the
 * <script> tag and this module, and we adopt its queue here.
 */

import HostLanguage from "./HostLanguage";

export type HostEvent = "ready" | "open" | "close" | "message" | "reply";

/** Where the widget should appear to grow from. */
export interface OpenOptions {
  origin?: HTMLElement | { x: number; y: number } | null;
}

export interface SendMessageOptions extends OpenOptions {
  /** Open the widget first. Default true — a message nobody can see is a bug. */
  open?: boolean;
}

export interface HostApiHandlers {
  open: (options?: OpenOptions) => void;
  close: () => void;
  toggle: (options?: OpenOptions) => void;
  sendMessage: (text: string, options?: SendMessageOptions) => void;
  isOpen: () => boolean;
}

type QueuedCall = [string, any[]];

const DOM_EVENT_PREFIX = "plutonios-chat:";

let handlers: HostApiHandlers | null = null;
let queue: QueuedCall[] = [];
const listeners = new Map<HostEvent, Set<(payload: any) => void>>();

/** Resolve an origin hint to viewport coordinates. */
export const resolveOrigin = (
  origin: OpenOptions["origin"]
): { x: number; y: number } | null => {
  if (!origin) return null;

  if (typeof (origin as HTMLElement).getBoundingClientRect === "function") {
    const rect = (origin as HTMLElement).getBoundingClientRect();
    // An element that is not laid out (display:none, detached) reports an
    // all-zero rect, which would animate the widget out of the top-left corner
    // instead of the button the guest actually clicked.
    if (!rect.width && !rect.height) return null;
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  const point = origin as { x: number; y: number };
  if (typeof point.x === "number" && typeof point.y === "number") return point;

  return null;
};

const emit = (event: HostEvent, payload: any = {}) => {
  listeners.get(event)?.forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      // A throwing host listener must not take the widget down with it.
      console.error(`PlutoniosChat: "${event}" listener failed`, error);
    }
  });

  // Also as a DOM event, so a host page can listen without holding a reference
  // to our object (useful from tag managers and inline handlers).
  try {
    window.dispatchEvent(
      new CustomEvent(`${DOM_EVENT_PREFIX}${event}`, { detail: payload })
    );
  } catch {
    /* no CustomEvent constructor: not a browser we support anyway */
  }
};

const on = (event: HostEvent, listener: (payload: any) => void) => {
  if (typeof listener !== "function") return api;
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event)!.add(listener);
  return api;
};

const off = (event: HostEvent, listener: (payload: any) => void) => {
  listeners.get(event)?.delete(listener);
  return api;
};

/** Run now if the widget is mounted, otherwise remember it for when it is. */
const invoke = (method: keyof HostApiHandlers, args: any[]) => {
  if (!handlers) {
    queue.push([method, args]);
    return api;
  }
  (handlers[method] as any)(...args);
  return api;
};

const api = {
  open: (options?: OpenOptions) => invoke("open", [options]),
  close: () => invoke("close", []),
  toggle: (options?: OpenOptions) => invoke("toggle", [options]),
  sendMessage: (text: string, options?: SendMessageOptions) => {
    if (typeof text !== "string" || !text.trim()) {
      console.warn("PlutoniosChat.sendMessage: a non-empty string is required");
      return api;
    }
    return invoke("sendMessage", [text.trim(), options]);
  },
  /**
   * Switch the widget's language without remounting it: the conversation, the
   * open state and the thread stay. Not queued behind the mount like the calls
   * above — a language named before the widget exists is the one it starts in.
   */
  setLanguage: (language: string) => {
    HostLanguage.set(language);
    return api;
  },
  on,
  off,
  isOpen: () => (handlers ? handlers.isOpen() : false),
  isReady: () => handlers !== null,
};

export type PlutoniosChatApi = typeof api;

const HostApi = {
  /**
   * Publish the API and adopt whatever the embed script's stub collected.
   * Safe to call more than once (two embeds of the same widget).
   */
  install() {
    const existing = (window as any).PlutoniosChat;

    if (existing && existing !== api) {
      // The stub records calls as [method, args]; replay them through the real
      // object, which either runs them (already mounted) or re-queues them.
      const pending: QueuedCall[] = existing._queue ?? [];
      (window as any).PlutoniosChat = api;
      pending.forEach(([method, args]) => {
        const fn = (api as any)[method];
        if (typeof fn === "function") fn(...args);
      });
      return;
    }

    (window as any).PlutoniosChat = api;
  },

  /** The mounted widget takes over; anything queued runs now, in order. */
  connect(next: HostApiHandlers) {
    handlers = next;

    // Before draining, so a host listening for "ready" is not told the widget
    // is available only after it has already reported opening.
    emit("ready", {});

    const pending = queue;
    queue = [];
    pending.forEach(([method, args]) => {
      const fn = (handlers as any)?.[method];
      if (typeof fn === "function") fn(...args);
    });
  },

  /** Back to queueing — the widget unmounted (host removed it, or React did). */
  disconnect() {
    handlers = null;
  },

  emit,
};

export default HostApi;
